/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Modal,
  Chip,
  Divider,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { ClearIcon } from "@mui/x-date-pickers";
import { fetchDomainApproverVehicles } from "../../Services/redux/Vehicles-details/VehiclesDetailsActions";
import { fetchCountriesDetails } from "../../Services/redux/Country-details/CountryDetailsActions";
import { putSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchDomainApproverEcuDetails } from "../../Services/redux/domain-approver-ecu-types/DomainApproverEcuActions";
import DomainApproverTargetingMmyPopUp from "./DomainApproverTargetingMmyPopUp";
import DomainApproverTargetEcuTypePopup from "./DomainApproverTargetEcuTypePopup";
import "./DomainApproverApplicationDetailsTargeting.css";
import { fetchDomainApproverHeadUnitDetails } from "../../Services/redux/domain-approver-head-unit/DomainApproverHeadUnitActions";
import DomainApproverHeadUnitPopUp from "../../page/domain-approver/domain-approver-headunitpopup/domainApproverHeadUnitPopUp";
import CPUTypeModal from "./CPUTypeModal";
import { fetchCpuDetails } from "../../Services/redux/CPU-details/CpuDetailsActions";
import CPULoader from "./CPULoader";
import CloseIcon from "@mui/icons-material/Close";
import ApplicationModal from "../ApplicationModal/ApplicationModal";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { getConfig } from "../../config";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "5px",
  p: 4,
};

const RESTRICTED_STATUSES = new Set(["IN_REVIEW", "SUBMITTED_FOR_STAGING", "SUBMITTED_FOR_BETA"]);

function sortByName(items) {
  return [...items].sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
}

function resolveCountriesFromTargeting(targettingData, countries, thirdPartyApps) {
  if (!countries?.length || !targettingData || Object.keys(targettingData).length === 0) {
    return [];
  }

  if (!thirdPartyApps) {
    const inclusions = targettingData.targeting?.countriesInclusions;
    if (!inclusions?.length) return [];
    const mapped = inclusions
      .map((countryId) => countries.find((c) => c.id === countryId))
      .filter(Boolean);
    return sortByName(mapped);
  }

  const countryList =
    targettingData.tags?.find((item) => item.key === "country")?.values || [];
  if (!countryList.length || !targettingData.tags?.length) return [];
  const mapped = countryList
    .map((countryId) => countries.find((c) => c.iso3Code === countryId))
    .filter(Boolean);
  return sortByName(mapped);
}

function computeDSACompliance(targetCountries, europeanCountries) {
  if (!europeanCountries || !Array.isArray(targetCountries)) return false;
  return targetCountries.some((countryId) => europeanCountries.includes(countryId));
}

function getHeadUnitDisplayText(selectedHeadUnits, headUnit) {
  if (selectedHeadUnits.length === 0) return "All Available";
  if (selectedHeadUnits.length > 3) {
    return `${selectedHeadUnits.slice(0, 3).join(", ")} and ${selectedHeadUnits.length - 3} more`;
  }
  const updatedHeadUnit = headUnit
    .filter((model) => !selectedHeadUnits.includes(model.id || model))
    .map((model) => model.name);
  return updatedHeadUnit.length > 0 ? updatedHeadUnit.join(", ") : "Not Selected";
}

function isReadOnlyDisabled(isRestricted, readOnlyGlobalAdmin) {
  return (
    isRestricted &&
    (sessionStorage.getItem("readonly_DomainApprover") === "true" || readOnlyGlobalAdmin)
  );
}

function DomainApproverApplicationDetailsTargeting({
  targettingData,
  appId,
  appData,
}) {
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  const [vehicleData, setVehicleData] = useState([]);
  const [openHeadUnitPopup, setOpenHeadUnitPopup] = useState(false);
  const [selectedHeadUnits, setSelectedHeadUnits] = useState([]);
  const [loader, setLoader] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [openMmyPopup, setOpenMmyPopup] = useState(false);
  const [openEcuPopup, setOpenEcuPopup] = useState(false);
  const [mmyExclusions, setMmyExclusions] = useState([]);
  const [openSeeMoreCountries, setOpenSeeMoreCountries] = useState(false);
  const [openCPUTypeModal, setOpenCPUTypeModal] = useState(false);
  const [shouldShowCpuLoader, setShouldShowCpuLoader] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [countris, setCountris] = useState([]);
  const [showCountries, setShowCountries] = useState([]);
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");

  const dispatch = useDispatch();
  const { submissionId } = useParams();
  const { headUnit, loading, error } = useSelector(
    (state) => state.domainApproverHeadUnit,
  );
  const vehiclesState = useSelector((state) => state.vehiclesDetails);
  const submissionState = useSelector((state) => state.submissionDetails);
  const ecuTypeData = useSelector((state) => state.domainApproverEcuTypes.ecu);
  const { loading: loadingCpuData } = useSelector((state) => state.cpuDetails);
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails,
  );
  const countries = useSelector((state) => state.countryDetails.countries);
  const { architechures: allCpuTypes } = useSelector(
    (state) => state.cpuDetails,
  );

  useEffect(() => {
    if (appData?.domain?.domainAliasId) {
      dispatch(
        fetchDomainApproverHeadUnitDetails(appData.domain.domainAliasId),
      );
    }
    setSelectedHeadUnits(targettingData?.targeting?.headunitsExclusions || []);
  }, [dispatch, appData, targettingData]);

  // Handle opening head unit popup
  const handleHeadUnitViewClick = () => {
    setOpenHeadUnitPopup(true);
    if (appData?.domain?.domainAliasId) {
      dispatch(
        fetchDomainApproverHeadUnitDetails(appData.domain.domainAliasId),
      );
    } else {
      console.warn(
        "Cannot fetch head unit details: appData.domain.domainAliasId is undefined",
      );
    }
  };

  // Handler for saving head unit models - Make it async
  const handleHeadUnitSave = async (unSelectedModels) => {
    setLoader(true);

    try {
      const updatedData = {
        ...targettingData,
        targeting: {
          ...targettingData.targeting,
          headunitsExclusions: unSelectedModels,
        },
      };

      await Promise.resolve(dispatch(putSubmissionDetails(appId, submissionId, updatedData)));

      setOpenHeadUnitPopup(false);
    } catch (err) {
      console.error("Error saving head unit data:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleHeadUnitClose = () => {
    setOpenHeadUnitPopup(false);
  };

  const handleClose = () => {
    setOpenPopup(false);
  };
  const changeCountris = () => {
    const newCountriesArray = submissionData.targeting.countriesInclusions
      .map((countryId) => countries.find((country) => country.id === countryId))
      .filter(Boolean);
    setShowCountries(newCountriesArray.slice(0, 20));
    setCountris(newCountriesArray);
  };
  const sendPostSubmissionCall = async (
    selectedData,
    reasonForCountryExclusion,
  ) => {
    setLoader(true);

    try {
      const targetCountries = selectedData.map((country) => country.id);
      const europeanCountries = getConfig().REACT_APP_EUROPEAN_COUNTRIES;
      const isDSACompliant = computeDSACompliance(targetCountries, europeanCountries);

      const { legalDocuments, ...rest } = submissionData;
      const newSubmissionData = {
        ...rest,
        targeting: {
          ...rest.targeting,
          countriesInclusions: [...targetCountries],
        },
        isDSACompliant,
        reasonForCountryExclusion,
      };

      await Promise.resolve(dispatch(
        putSubmissionDetails(appId, submissionId, newSubmissionData),
      ));
    } catch (err) {
      console.error("Error saving countries data:", err);
    } finally {
      setLoader(false);
    }
  };

  const portal = getConfig().REACT_APP_PORTAL;
  const isRestricted =
    (portal === "global-admin" ||
      (portal === "domain-approver" && !thirdPartyApps)) &&
    RESTRICTED_STATUSES.has(targettingData?.status);
  const isDisabled = isReadOnlyDisabled(isRestricted, readOnly_GlobalAdmin);

  useEffect(() => {
    dispatch(fetchCountriesDetails());
  }, []);

  useEffect(() => {
    const resolved = resolveCountriesFromTargeting(targettingData, countries, thirdPartyApps);
    setShowCountries(resolved.slice(0, 20));
    setCountris(resolved);
  }, [countries, targettingData]);

  // Reset CPU loader when loading completes
  useEffect(() => {
    if (!loadingCpuData) {
      setShouldShowCpuLoader(false);
    }
  }, [loadingCpuData]);

  const handleCloseCPUTypeModal = () => {
    setOpenCPUTypeModal(false);
    setShouldShowCpuLoader(false);
  };

  const viewCPUTypeModal = () => {
    setOpenCPUTypeModal(true);

    if (isRestricted || !allCpuTypes || allCpuTypes.length === 0) {
      setShouldShowCpuLoader(true);
      dispatch(fetchCpuDetails());
    } else {
      setShouldShowCpuLoader(false);
    }
  };

  useEffect(() => {
    const domainAliasId = appData?.domain?.domainAliasId;

    if (thirdPartyApps) {
      sessionStorage.setItem("domain", "VWGroup");
      dispatch(fetchDomainApproverEcuDetails());
      if (domainAliasId) {
        dispatch(fetchDomainApproverVehicles(domainAliasId));
      }
    } else if (domainAliasId) {
      dispatch(fetchDomainApproverVehicles(domainAliasId));
      sessionStorage.setItem("domain", "VWGroup");
      dispatch(fetchDomainApproverEcuDetails());
    }
  }, [
    dispatch,
    appData?.domain?.domainAliasId,
    appId,
    thirdPartyApps,
    appData,
  ]);

  // Initialize mmyExclusions from existing targeting data
  useEffect(() => {
    // Check both singular and plural versions for compatibility
    const exclusions =
      targettingData?.targeting?.vehicleExclusions ||
      targettingData?.targeting?.vehiclesExclusions ||
      [];

    setMmyExclusions(exclusions);
  }, [
    targettingData?.targeting?.vehicleExclusions,
    targettingData?.targeting?.vehiclesExclusions,
  ]);

  const handleTargetingSave = async (type, key, data, modalCloseFn) => {
    setLoader(true);

    try {
      const updatedTargeting = {
        ...targettingData.targeting,
        [key]: data,
      };

      const payload = {
        ...targettingData,
        targeting: updatedTargeting,
      };

      await Promise.resolve(dispatch(putSubmissionDetails(appId, targettingData.id, payload)));
    } catch (err) {
      console.error("Error saving targeting data:", err);
    } finally {
      modalCloseFn();
      setLoader(false);
    }
  };
  const handleSaveData = async (type, data) => {
    if (type === "mmy_inclusion") {
      setOpenMmyPopup(false);

      setLoader(true);

      try {
        const { vehicleInclusions, vehicleExclusions } = data;

        const updatedTargeting = {
          ...targettingData.targeting,
          vehiclesInclusions: vehicleInclusions,
          vehiclesExclusions: vehicleExclusions,
        };

        const payload = {
          ...targettingData,
          targeting: updatedTargeting,
        };

        setMmyExclusions(vehicleExclusions);

        await Promise.resolve(dispatch(putSubmissionDetails(appId, targettingData.id, payload)));
      } catch (err) {
        console.error("Error saving MMY data:", err);
      } finally {
        setLoader(false);
      }
    }
  };

  // Handle ECU popup save
  const handleEcuSave = (key, values) => {
    handleTargetingSave("ecu", key, values, () => setOpenEcuPopup(false));
  };

  // Handle CPU types save
  const handleCpuSave = (key, values) => {
    handleTargetingSave("cpu", key, values, () => setOpenCPUTypeModal(false));
  };

  useEffect(() => {
    if (targettingData.targeting && vehiclesState.domainApproverVehicles.data) {
      const IncludedMMYdata = targettingData?.targeting?.vehiclesInclusions;
      const allMMYdata = vehiclesState.domainApproverVehicles.data;
      const IncludedMmyDataId = IncludedMMYdata.map((item) => item.make.id);
      if (IncludedMmyDataId[0] === null || IncludedMmyDataId[0] === undefined) {
        setVehicleData([]);
      } else {
        const allMMYdataId = allMMYdata.map((item) => item.id);
        const includedSet = new Set(IncludedMmyDataId);
        const uniqueMmyId = new Set(allMMYdataId.filter(
          (item) => !includedSet.has(item),
        ));

        const excludedMmyDataTargetingData = allMMYdata.filter((item) =>
          uniqueMmyId.has(item.id),
        );

        const excludedMmyTargetingModels = excludedMmyDataTargetingData.flatMap(
          (item) => item.models,
        );
        setVehicleData(excludedMmyTargetingModels);
      }
    }
  }, [targettingData, vehiclesState.domainApproverVehicles.data]);

  const transformedVehiclesData = React.useMemo(() => {
    const vehiclesData = vehiclesState.domainApproverVehicles.data;
    const mmyDictionaryData = targettingData?.mmyDictionary;

    if (
      (!vehiclesData || !Array.isArray(vehiclesData)) &&
      mmyDictionaryData &&
      Object.keys(mmyDictionaryData).length > 0
    ) {
      return mmyDictionaryData.map((vehicle) => ({
        make: vehicle.make,

        models: vehicle.models
          ? vehicle.models.map((model) => ({
              ...model,
            }))
          : [],
      }));
    }
    if (!vehiclesData || !Array.isArray(vehiclesData)) {
      return [];
    }
    return vehiclesData.map((vehicle) => ({
      make: vehicle.name,
      id: vehicle.id,
      models: vehicle.models
        ? vehicle.models.map((model) => ({
            ...model,
            id: model.id,
          }))
        : [],
    }));
  }, [
    vehiclesState.domainApproverVehicles.data,
    targettingData?.mmyDictionary,
  ]);

  // Transform exclusions from object format to string format expected by popup
  const transformedExclusions = React.useMemo(() => {
    if (!mmyExclusions || !Array.isArray(mmyExclusions)) {
      return [];
    }
    return new Array(mmyExclusions.length).fill(null);
  }, [mmyExclusions]);

  // Transform existing inclusions data for popup initialization
  const transformedInclusions = React.useMemo(() => {
    if (
      !targettingData?.targeting?.vehiclesInclusions ||
      !Array.isArray(targettingData.targeting.vehiclesInclusions)
    ) {
      return [];
    }

    return targettingData.targeting.vehiclesInclusions;
  }, [targettingData?.targeting?.vehiclesInclusions]);

  // Extract ECU data from targeting
  const ecuInclusions = React.useMemo(() => {
    return targettingData?.targeting?.ecuTypeInclusions || [];
  }, [targettingData?.targeting?.ecuTypeInclusions]);

  const ecuExclusions = React.useMemo(() => {
    return targettingData?.targeting?.ecuType_exclusion || [];
  }, [targettingData?.targeting?.ecuType_exclusion]);

  // Extract CPU architecture data from targeting
  const cpuArchitectureInclusions = React.useMemo(() => {
    return targettingData?.targeting?.architectureTypesInclusions || [];
  }, [targettingData?.targeting?.architectureTypesInclusions]);

  const isAllCpuTypesSelected = React.useMemo(() => {
    if (!allCpuTypes || allCpuTypes.length === 0) return false;
    return (
      cpuArchitectureInclusions.length === allCpuTypes.length &&
      allCpuTypes.every((cpu) => cpuArchitectureInclusions.includes(cpu))
    );
  }, [cpuArchitectureInclusions, allCpuTypes]);

  const getCpuTypeDisplayText = () => {
    if (isAllCpuTypesSelected) return "All available";
    return cpuArchitectureInclusions.join(", ");
  };

  useEffect(() => {
    const isVehicleDataLoaded =
      (vehiclesState.domainApproverVehicles.data &&
        vehiclesState.domainApproverVehicles.data.length > 0) ||
      targettingData?.mmyDictionary;
    const isCountriesLoaded = countries && countries.length > 0;
    const isTargetingDataLoaded =
      targettingData && Object.keys(targettingData).length > 0;

    if (isVehicleDataLoaded && isCountriesLoaded && isTargetingDataLoaded) {
      setDataLoading(false);
    } else {
      setDataLoading(true);
    }
  }, [vehiclesState.domainApproverVehicles.data, countries, targettingData]);

  return (
    <>
      {dataLoading && (
        <Backdrop
          open={dataLoading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {loader && (
        <Backdrop
          open={loader}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div id="targeting" className="domain-approver-targetting-main-section">
        <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
          Targeting
        </Typography>

        <div className="domain-approver-targetting-content">
          {/* Countries Section */}
          <div className="domain_targeted_countries">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 5px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                  }}
                >
                  Countries <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
                <IconButton>
                  <Info
                    sx={{ height: "14px", width: "14px", color: "#91979A" }}
                  />
                </IconButton>
              </Box>
              <Box>
                <Button
                  className="view-button"
                  variant="outlined"
                  style={{ textTransform: "none" }}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                  }}
                  onClick={() => {
                    setOpenPopup(true);
                  }}
                  disabled={isDisabled}
                >
                  {isRestricted ? "Set" : "View"}
                </Button>
              </Box>
            </Box>
            <div className="domain_targeted_countries_chip">
              {showCountries?.length > 0 &&
                showCountries.map((country) => (
                  <Chip
                    key={country.id || country.name}
                    label={country.name}
                    sx={{
                      marginLeft: "4px",
                      width: "max-content",
                      borderRadius: "4px",
                      backgroundColor: "#F3F3F3",
                    }}
                    size="small"
                  />
                ))}
              {countris && countris.length > 8 && (
                <Typography
                  sx={{
                    cursor: "pointer",
                    color: "rgb(21, 101, 192)",
                    fontSize: "12px",
                  }}
                  onClick={() => setOpenSeeMoreCountries(true)}
                >
                  See More
                </Typography>
              )}
            </div>
          </div>

          {/* Head Unit Models Section - Updated */}
          <div className="domain_targeted_head-unit">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                  Head Unit Models <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
                <IconButton>
                  <Info
                    sx={{ height: "14px", width: "14px", color: "#91979A" }}
                  />
                </IconButton>
              </Box>
              <Box>
                <Button
                  className="view-button"
                  variant="outlined"
                  style={{ textTransform: "none" }}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                  }}
                  disabled={isDisabled}
                  onClick={handleHeadUnitViewClick}
                >
                  {isRestricted ? "Set" : "View"}
                </Button>
              </Box>
            </Box>
            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {getHeadUnitDisplayText(selectedHeadUnits, headUnit || [])}
            </Typography>
          </div>

          {/* Make/Model/Year Section */}
          <div className="domain_targeted_make-model">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                  Make/Model/Year <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
                <IconButton>
                  <Info
                    sx={{ height: "14px", width: "14px", color: "#91979A" }}
                  />
                </IconButton>
              </Box>
              <Box>
                <Button
                  className="view-button"
                  variant="outlined"
                  style={{ textTransform: "none" }}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                  }}
                  onClick={() => setOpenMmyPopup(true)}
                  disabled={isDisabled}
                >
                  {isRestricted ? "Set" : "View"}
                </Button>
              </Box>
            </Box>
            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {`Excluded (${
                vehicleData.length + transformedExclusions.length
              })`}
            </Typography>
          </div>

          {/* CPU Type Section */}
          <div className="domain_targeted_cpu-type">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                  CPU Type <span style={{ color: "#B50B00" }}>*</span>
                </Typography>
                <IconButton>
                  <Info
                    sx={{ height: "14px", width: "14px", color: "#91979A" }}
                  />
                </IconButton>
              </Box>
              <Box>
                <Button
                  onClick={viewCPUTypeModal}
                  className="view-button"
                  variant="outlined"
                  style={{ textTransform: "none" }}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                  }}
                  disabled={isDisabled}
                >
                  {isRestricted ? "Set" : "View"}
                </Button>

                {shouldShowCpuLoader && loadingCpuData ? (
                  <CPULoader open={shouldShowCpuLoader && loadingCpuData} />
                ) : (
                  <CPUTypeModal
                    openCPUTypeModal={openCPUTypeModal}
                    handleCloseCPUTypeModal={handleCloseCPUTypeModal}
                    disabledVal={!isRestricted}
                    onSave={handleCpuSave}
                    existingSelections={cpuArchitectureInclusions}
                  />
                )}
              </Box>
            </Box>
            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {cpuArchitectureInclusions.length === 0
                ? "Not Selected"
                : getCpuTypeDisplayText()}
            </Typography>
          </div>

          {/* ECU Type Section */}
          <div className="domain_targeted_ecu-type">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 15px 0px 15px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                  ECU Type
                </Typography>
                <IconButton>
                  <Info
                    sx={{ height: "14px", width: "14px", color: "#91979A" }}
                  />
                </IconButton>
              </Box>
              <Box>
                <Button
                  className="view-button"
                  variant="outlined"
                  style={{ textTransform: "none" }}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                  }}
                  onClick={() => setOpenEcuPopup(true)}
                  disabled={isDisabled}
                >
                  {isRestricted ? "Set" : "View"}
                </Button>
              </Box>
            </Box>
            <Typography
              sx={{
                paddingLeft: "15px",
                fontSize: "12px",
                fontWeight: "400",
                color: "#5A5B5C",
                paddingBottom: "10px",
              }}
            >
              {targettingData?.targeting?.ecuTypeInclusions.join(", ") ||
                "Not Selected"}
            </Typography>
          </div>
        </div>
      </div>

      {/* Make/Model/Year Popup */}
      {openMmyPopup && (
        <Modal
          open={openMmyPopup}
          onClose={() => setOpenMmyPopup(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              borderRadius: "8px",
              p: 3,
              maxWidth: "80vw",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <DomainApproverTargetingMmyPopUp
              mmyData={transformedVehiclesData}
              mmyExclusion={transformedExclusions}
              mmyInclusion={transformedInclusions}
              mmySave={handleSaveData}
              onClose={() => setOpenMmyPopup(false)}
              reviewStatus={submissionState.loading ? ["SAVING"] : []}
              readOnly={!isRestricted}
              isThirdPartyApp={thirdPartyApps}
            />
          </Box>
        </Modal>
      )}

      {/* ECU Type Popup */}
      {openEcuPopup && (
        <DomainApproverTargetEcuTypePopup
          open={openEcuPopup}
          onClose={() => setOpenEcuPopup(false)}
          vehiclesData={transformedVehiclesData}
          ecuTypeData={ecuTypeData}
          exclusions={ecuExclusions}
          inclusions={ecuInclusions}
          onSave={handleEcuSave}
          reviewStatus={submissionState.loading ? ["SAVING"] : []}
          readOnly={!isRestricted}
        />
      )}
      {/* See More Countries Modal */}
      <Modal
        open={openSeeMoreCountries}
        onClose={() => setOpenSeeMoreCountries(false)}
      >
        <Box
          className="see-more-countries-popup"
          sx={{
            width: "490px",
            maxHeight: "435px",
            backgroundColor: "white",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "4px",
          }}
        >
          <div className="see-more-countries-popup-content">
            <div className="see-more-countries-popup-header">
              <Typography
                sx={{
                  fontWeight: "500",
                  fontSize: "20px",
                  marginLeft: "7px",
                }}
              >
                Countries
              </Typography>
              <IconButton onClick={() => setOpenSeeMoreCountries(false)}>
                <ClearIcon />
              </IconButton>
            </div>
            <Divider sx={{ marginTop: "10px !important" }} />
            <div className="see-more-countries-popup-body">
              {countris?.map((country) => (
                  <Chip
                    key={country.id || country.name}
                    label={country.name}
                    sx={{
                      marginLeft: "8px",
                      marginBottom: "8px",
                      width: "max-content",
                      borderRadius: "4px",
                      backgroundColor: "#F3F3F3",
                    }}
                    size="small"
                  />
                ))}
            </div>
          </div>
        </Box>
      </Modal>

      {/* Head Unit Models Popup */}
      <DomainApproverHeadUnitPopUp
        open={openHeadUnitPopup}
        onClose={handleHeadUnitClose}
        selectedModels={selectedHeadUnits}
        onSave={handleHeadUnitSave}
        headUnitData={headUnit || []}
        isRestricted={isRestricted}
        loading={loading}
        error={error}
        targettingData={targettingData}
      />
      <Modal
        open={openPopup}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={{ ...style, width: 700, height: 530 }}>
          <h2 id="parent-modal-title">Targeting-Countries</h2>
          <div style={{ position: "absolute", right: "30px", top: "45px" }}>
            <IconButton>
              <CloseIcon onClick={handleClose} />
            </IconButton>
          </div>
          <section>
            <ApplicationModal
              handleClose={handleClose}
              selectedData={countris}
              availabilty={countries}
              name="Countries"
              status={submissionData.status}
              changeCountris={changeCountris}
              sendPostSubmissionCall={sendPostSubmissionCall}
              readOnly={!isRestricted}
            />
          </section>
        </Box>
      </Modal>
    </>
  );
}

DomainApproverApplicationDetailsTargeting.propTypes = {
  targettingData: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    targeting: PropTypes.shape({
      countriesInclusions: PropTypes.array,
      headunitsExclusions: PropTypes.array,
      vehiclesInclusions: PropTypes.array,
      vehiclesExclusions: PropTypes.array,
      vehicleExclusions: PropTypes.array,
      ecuTypeInclusions: PropTypes.array,
      ecuType_exclusion: PropTypes.array,
      architectureTypesInclusions: PropTypes.array,
    }),
    tags: PropTypes.array,
    mmyDictionary: PropTypes.array,
  }),
  appId: PropTypes.string,
  appData: PropTypes.shape({
    domain: PropTypes.shape({
      domainAliasId: PropTypes.string,
    }),
  }),
};

export default DomainApproverApplicationDetailsTargeting;
